import { supabase } from '@/lib/supabase';
import { validateUsername } from '@/lib/username';

// ─────────────────────────────────────────────────────────────
// SuperAdmin — Équipe plateforme.
// Le superadmin principal crée des comptes travailleurs qui accèdent au
// back-office (/superadmin) avec des permissions précises. Le compte est
// un utilisateur Zifek (typecompte = 1, comme un superadmin) MAIS ses accès
// sont restreints par la ligne platform_team (permissions jsonb).
// ─────────────────────────────────────────────────────────────

export interface PlatformTeamMember {
  id: number;
  user_id: number;
  role: string;
  role_label: string | null;
  permissions: string[];
  status: string;
  created_by: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface PlatformTeamRow extends PlatformTeamMember {
  name: string;
  user_name: string;
  email: string;
  telephone: string;
}

export interface CreatePlatformStaffInput {
  name: string;
  email: string;
  telephone?: string;
  password: string;
  roleLabel: string;
  permissions: string[];
  createdBy?: number | null;
}

function usernameSeed(email: string, name: string): string {
  const fromEmail = (email.split('@')[0] || '').toLowerCase();
  const raw = fromEmail || name.toLowerCase() || 'staff';
  return raw.replace(/[^a-z0-9._-]/g, '') || 'staff';
}

async function uniqueUsername(seed: string): Promise<string> {
  const { valid, normalized } = validateUsername(seed);
  const base = valid ? normalized : `staff${Date.now().toString().slice(-6)}`;
  let candidate = base;
  for (let i = 0; i < 6; i += 1) {
    const { data } = await supabase.from('users').select('id').ilike('user_name', candidate).limit(1);
    if (!data || data.length === 0) return candidate;
    candidate = `${base}${Math.floor(Math.random() * 900 + 100)}`;
  }
  return `${base}${Date.now().toString().slice(-4)}`;
}

/** Liste les membres de l'équipe plateforme, enrichis des infos utilisateur. */
export async function listPlatformTeam(): Promise<PlatformTeamRow[]> {
  const { data, error } = await supabase
    .from('platform_team')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  const rows = (data || []) as PlatformTeamMember[];
  if (rows.length === 0) return [];

  const ids = rows.map((r) => r.user_id).filter((n) => Number.isFinite(n));
  const labels: Record<number, { name: string; user_name: string; email: string; telephone: string }> = {};
  if (ids.length > 0) {
    const { data: users } = await supabase
      .from('users')
      .select('id, name, user_name, email, telephone')
      .in('id', ids);
    (users || []).forEach((u) => {
      const r = u as { id: number; name: string | null; user_name: string | null; email: string | null; telephone: string | null };
      labels[r.id] = {
        name: r.name || `Utilisateur #${r.id}`,
        user_name: r.user_name || '',
        email: r.email || '',
        telephone: r.telephone || '',
      };
    });
  }

  return rows.map((r) => ({
    ...r,
    permissions: Array.isArray(r.permissions) ? r.permissions : [],
    name: labels[r.user_id]?.name || `Utilisateur #${r.user_id}`,
    user_name: labels[r.user_id]?.user_name || '',
    email: labels[r.user_id]?.email || '',
    telephone: labels[r.user_id]?.telephone || '',
  }));
}

/** Crée le compte travailleur plateforme puis sa ligne d'accès. */
export async function createPlatformStaff(input: CreatePlatformStaffInput): Promise<number> {
  const email = input.email.trim().toLowerCase();
  const name = input.name.trim();
  if (!name) throw new Error('Le nom du travailleur est requis.');
  if (!email) throw new Error("L'email est requis.");
  if (!input.password || input.password.length < 6) {
    throw new Error('Le mot de passe doit contenir au moins 6 caractères.');
  }

  const { data: existing } = await supabase.from('users').select('id').eq('email', email);
  if (existing && existing.length > 0) {
    throw new Error('Cet email est déjà utilisé par un autre compte.');
  }

  const username = await uniqueUsername(usernameSeed(email, name));
  const { default: bcrypt } = await import('bcryptjs');
  const hashed = bcrypt.hashSync(input.password, bcrypt.genSaltSync(10));

  const { data, error } = await supabase
    .from('users')
    .insert({
      user_name: username,
      email,
      password: hashed,
      name,
      telephone: input.telephone?.trim() || '',
      type: 'SuperAdmin',
      typecompte: 1,
      monaie: 'MAD',
      langue: 'fr',
      active: 1,
      package: 0,
      payant: 0,
    })
    .select('id')
    .maybeSingle();
  if (error || !data?.id) {
    throw new Error('Impossible de créer le compte du travailleur. Réessayez.');
  }
  const userId = data.id as number;

  const { error: teamErr } = await supabase.from('platform_team').insert({
    user_id: userId,
    role: 'staff',
    role_label: input.roleLabel || 'Agent plateforme',
    permissions: input.permissions,
    status: 'active',
    created_by: input.createdBy ?? null,
  });
  if (teamErr) {
    // On ne laisse pas un compte orphelin sans accès.
    await supabase.from('users').delete().eq('id', userId);
    throw new Error("Compte créé mais l'attribution des permissions a échoué.");
  }

  return userId;
}

export interface UpdatePlatformStaffInput {
  roleLabel?: string;
  permissions?: string[];
  status?: string;
}

export async function updatePlatformStaff(id: number, patch: UpdatePlatformStaffInput): Promise<void> {
  const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.roleLabel !== undefined) payload.role_label = patch.roleLabel;
  if (patch.permissions !== undefined) payload.permissions = patch.permissions;
  if (patch.status !== undefined) payload.status = patch.status;
  const { error } = await supabase.from('platform_team').update(payload).eq('id', id);
  if (error) throw error;
}

export async function setPlatformStaffStatus(id: number, status: string): Promise<void> {
  await updatePlatformStaff(id, { status });
}

export async function deletePlatformStaff(id: number): Promise<void> {
  const { error } = await supabase.from('platform_team').delete().eq('id', id);
  if (error) throw error;
}

/** Réinitialise le mot de passe d'un travailleur plateforme. */
export async function resetPlatformStaffPassword(userId: number, password: string): Promise<void> {
  if (!password || password.length < 6) {
    throw new Error('Le mot de passe doit contenir au moins 6 caractères.');
  }
  const { default: bcrypt } = await import('bcryptjs');
  const hashed = bcrypt.hashSync(password, bcrypt.genSaltSync(10));
  const { error } = await supabase.from('users').update({ password: hashed }).eq('id', userId);
  if (error) throw error;
}